import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, Badge } from '@/components/atoms';
import { Input } from '@kisanlink/ui-commons';
import { Pagination } from '@/components/molecules';
import { useWarehousesList, useDeleteWarehouseMutation } from '@/hooks/useErpApi';
import type { Warehouse } from '@/schemas/warehouse';

interface WarehouseListProps {
  onCreateClick: () => void;
  onEditClick?: (warehouse: Warehouse) => void;
}

export const WarehouseList: React.FC<WarehouseListProps> = ({ onCreateClick, onEditClick }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Calculate offset for API call
  const offset = (currentPage - 1) * itemsPerPage;
  const { data: warehouses, isLoading, error, refetch } = useWarehousesList({
    limit: itemsPerPage,
    offset,
  });
  const { mutate: deleteWarehouse } = useDeleteWarehouseMutation();

  const allStates = Array.from(
    new Set((warehouses || []).map((w) => w.address?.state || 'Unknown').filter(Boolean))
  ).sort();

  // Client-side filtering for search and state
  const filteredWarehouses = useMemo(() => {
    return (warehouses || []).filter((warehouse) => {
      const matchesSearch =
        warehouse.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (warehouse.address?.vtc || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (warehouse.address?.state || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (warehouse.address?.pincode || '').includes(searchQuery);

      const matchesState =
        selectedState === 'all' || (warehouse.address?.state || '') === selectedState;

      return matchesSearch && matchesState;
    });
  }, [warehouses, searchQuery, selectedState]);

  // Get total count from API response if available, otherwise use filtered length
  const totalWarehouses = (warehouses as any)?.total_count || filteredWarehouses.length;
  
  // Reset to page 1 when search or state changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };
  
  const handleStateChange = (state: string) => {
    setSelectedState(state);
    setCurrentPage(1);
  };

  const handleRowClick = (id: string) => {
    navigate(`/warehouses/${id}`);
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this warehouse?')) {
      try {
        await deleteWarehouse(id);
        refetch();
      } catch (error) {
        // Error handled by useDeleteWarehouseMutation
      }
    }
  };

  const handleEdit = (e: React.MouseEvent, warehouse: Warehouse) => {
    e.stopPropagation();
    if (onEditClick) {
      onEditClick(warehouse);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Icon name="Warehouse" size={48} className="text-neutral-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-neutral-600">Loading warehouses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-error-50 border border-error-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="bg-error-100 p-2 rounded-lg">
              <Icon name="Info" size={24} className="text-error-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-error-900 mb-1">
                Failed to Load Warehouses
              </h3>
              <p className="text-error-700 mb-3">
                Unable to fetch warehouses data. Please try again.
              </p>
              <Button variant="primary" size="sm" onClick={() => refetch()}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">Warehouses</h1>
          <p className="text-neutral-600 mt-2">Manage warehouse locations and inventory</p>
        </div>
        <Button variant="primary" className="sm:w-auto" onClick={onCreateClick}>
          <Icon name="Plus" size={20} className="mr-2" />
          Create Warehouse
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Icon
                name="Search"
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400"
              />
              <Input
                type="text"
                placeholder="Search by name, city, or pincode..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => handleStateChange('all')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                selectedState === 'all'
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              All States
            </button>
            {allStates.slice(0, 4).map((state) => (
              <button
                key={state}
                onClick={() => handleStateChange(state)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  selectedState === state
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                {state}
              </button>
            ))}
          </div>
        </div>
      </div>

      {filteredWarehouses.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-12 text-center">
          <Icon name="Warehouse" size={64} className="text-neutral-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-neutral-900 mb-2">No Warehouses Found</h3>
          <p className="text-neutral-600 mb-4">
            {searchQuery || selectedState !== 'all'
              ? 'Try adjusting your filters'
              : 'Create your first warehouse to get started'}
          </p>
          {!searchQuery && selectedState === 'all' && (
            <Button variant="primary" onClick={onCreateClick}>
              <Icon name="Plus" size={20} className="mr-2" />
              Create Warehouse
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Warehouse Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    State
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Pincode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {filteredWarehouses.map((warehouse) => (
                  <tr
                    key={warehouse.id}
                    onClick={() => handleRowClick(warehouse.id)}
                    className="hover:bg-neutral-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="bg-primary-100 p-2 rounded-lg mr-3">
                          <Icon name="Warehouse" size={20} className="text-primary-600" />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-neutral-900">
                            {warehouse.name}
                          </span>
                          {warehouse.address?.street && (
                            <p className="text-xs text-neutral-500">{warehouse.address.street}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-neutral-900">
                        {warehouse.address?.vtc || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-neutral-900">
                        {warehouse.address?.state || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-neutral-600">
                        {warehouse.address?.pincode || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="success">Active</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex gap-2 justify-end">
                        {onEditClick && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => handleEdit(e, warehouse)}
                          >
                            <Icon name="Pencil" size={16} className="mr-1" />
                            Edit
                          </Button>
                        )}
                        <Button
                          variant="error"
                          size="sm"
                          onClick={(e) => handleDelete(e, warehouse.id)}
                        >
                          <Icon name="Trash" size={16} className="mr-1" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalItems={totalWarehouses}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            disabled={isLoading}
          />
        </div>
      )}
    </div>
  );
};
